import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useCurrencies } from "../../../context/CurrenciesContext";
import { useLanguage } from "../../../context/LanguageContext";
import ProductsService from "../../../api/services/products.service";
import { ProductsTypes } from "../../../api/types/products.types";
import Loading from "../../../components/common/loading/Loading";
import ProductCard from "../../../components/common/product-card/ProductCard";
import TitleUpdater from "../../../components/common/title/TitleUpdater";
import { useTranslation } from "react-i18next";
import { groupProductVariants } from "../../../utils/groupProductVariants";
import styles from "./SearchResults.module.scss";

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const { currencyId } = useCurrencies();
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation("translation");

  const [products, setProducts] = useState<ProductsTypes[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const observerTarget = useRef<HTMLDivElement>(null);

  const PRODUCTS_PER_PAGE = 20;

  const getLocalizedName = (product: ProductsTypes) => {
    const langKey = `name_${currentLanguage}` as keyof ProductsTypes;
    const name = product[langKey];
    if (name && typeof name === "string") return name;
    return product.name_en || product.name_az || "";
  };

  const searchProducts = useCallback(
    async (pageNum: number = 1, append: boolean = false) => {
      if (!query.trim() || !currencyId) {
        return;
      }

      setLoading(true);
      try {
        const res = await ProductsService.searchProducts(
          query,
          currencyId,
          currentLanguage,
          pageNum,
          PRODUCTS_PER_PAGE,
        );

        const variants = (res.data.data || []) as ProductsTypes[];
        const rawCount = res.data.count || 0;

        // Group variants by product_id to get actual product count
        const groupedVariants = groupProductVariants(variants);
        const groupedCount = groupedVariants.length;

        if (append) {
          setProducts((prev) => {
            const newProducts = [...prev, ...variants];
            // Calculate total grouped count for all pages
            const allGrouped = groupProductVariants(newProducts);
            setHasMore(
              variants.length === PRODUCTS_PER_PAGE &&
                allGrouped.length < rawCount,
            );
            return newProducts;
          });
        } else {
          setProducts(variants);
          // For first page, estimate total grouped count based on raw count
          // This is an approximation since we don't know the exact grouped count until we fetch all
          setHasMore(
            variants.length === PRODUCTS_PER_PAGE && groupedCount < rawCount,
          );
        }

        // Note: totalCount will be updated by useEffect when products state changes
        setPage(pageNum);
      } catch (error) {
        console.error("Search error:", error);
      } finally {
        setLoading(false);
      }
    },
    [query, currencyId, currentLanguage],
  );

  useEffect(() => {
    if (query && currencyId) {
      setPage(1);
      setProducts([]);
      searchProducts(1, false);
    }
  }, [query, currencyId, currentLanguage, searchProducts]);

  // Infinite scroll
  useEffect(() => {
    if (!hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          searchProducts(page + 1, true);
        }
      },
      { threshold: 0.1 },
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loading, page, searchProducts]);

  const groupedProducts = groupProductVariants(products);

  // Update total count based on grouped products when products change
  useEffect(() => {
    if (products.length > 0) {
      const grouped = groupProductVariants(products);
      setTotalCount(grouped.length);
    } else if (products.length === 0) {
      setTotalCount(0);
    }
  }, [products]);

  return (
    <div className={styles.search_results}>
      <TitleUpdater title={t("search_results") || "Search Results"} />
      <div className={styles.container}>
        <div className={styles.header}>
          <h1>{t("search_results") || "Search Results"}</h1>
          {query && (
            <p className={styles.query_info}>
              {t("searching_for") || "Searching for"}:{" "}
              <strong>"{query}"</strong>
              {totalCount > 0 && (
                <span className={styles.count}>
                  {" "}
                  ({totalCount} {t("results") || "results"})
                </span>
              )}
            </p>
          )}
        </div>

        {loading && products.length === 0 ? (
          <Loading />
        ) : products.length > 0 ? (
          <>
            <div className={styles.products_list}>
              {groupedProducts.map((p, i) => (
                <ProductCard
                  id={p.id}
                  key={i}
                  title={getLocalizedName(p)}
                  currency={p.currency_name}
                  price={p.price?.toString()}
                  discount_percent={p.discount_percent?.toString()}
                  discount_price={p.discount_price?.toString()}
                  image={p.images ? p.images[0]?.image : ""}
                  stock_quantity={p.stock_quantity}
                  wishlist={p.is_wish}
                  images={p.images}
                  attributes={p.attributes}
                />
              ))}
            </div>
            {hasMore && (
              <div ref={observerTarget} className={styles.scroll_trigger}>
                {loading && <Loading />}
              </div>
            )}
          </>
        ) : (
          <div className={styles.no_results}>
            <p>{t("no_search_results") || "No products found"}</p>
            <p className={styles.suggestion}>
              {t("try_different_keywords") ||
                "Try different keywords or check your spelling"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResults;
